import React, { useMemo } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const BLOCK_DURATION_MS = 5 * 60 * 1000; // 5 minutes per column
const MAX_BLOCKS = 12; // Maximum time blocks to show

const EngagementHeatmapWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const sessionCreatedAt = state?.sessionCreatedAt ?? null;

  const heatmapData = useMemo(() => {
    const sessionAvatars = state?.sessionAvatars ?? [];
    const transcript = state?.transcript ?? [];
    if (!sessionCreatedAt || sessionAvatars.length === 0) return { avatars: [], blocks: 0, grid: new Map(), maxCount: 0 };

    const sessionStart = new Date(sessionCreatedAt).getTime();
    const now = Date.now();
    const elapsed = now - sessionStart;
    const blockCount = Math.min(MAX_BLOCKS, Math.max(1, Math.ceil(elapsed / BLOCK_DURATION_MS)));

    // Build grid: key = `${avatarRole}-${blockIndex}` → count
    const grid = new Map<string, number>();
    let maxCount = 0;

    for (const entry of transcript) {
      if (entry.speaker !== 'avatar') continue;
      const entryTime = new Date(entry.timestamp).getTime();
      const blockIndex = Math.min(blockCount - 1, Math.floor((entryTime - sessionStart) / BLOCK_DURATION_MS));
      const role = entry.avatarRole || '';
      const key = `${role}-${blockIndex}`;
      const count = (grid.get(key) || 0) + 1;
      grid.set(key, count);
      if (count > maxCount) maxCount = count;
    }

    return { avatars: sessionAvatars, blocks: blockCount, grid, maxCount };
  }, [state?.sessionAvatars, state?.transcript, sessionCreatedAt]);

  const { avatars, blocks, grid, maxCount } = heatmapData;

  if (avatars.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No data yet
      </div>
    );
  }

  const getIntensity = (count: number): string => {
    if (count === 0) return isDark ? 'bg-white/[0.03]' : 'bg-slate-50';
    const ratio = count / (maxCount || 1);
    if (ratio <= 0.25) return 'bg-primary/20';
    if (ratio <= 0.5) return 'bg-primary/40';
    if (ratio <= 0.75) return 'bg-primary/60';
    return 'bg-primary/80';
  };

  return (
    <div className="flex h-full px-3 py-2 gap-1">
      {/* Avatar labels column */}
      <div className="flex flex-col gap-[3px] shrink-0 justify-end pb-5">
        {avatars.map(avatar => (
          <div key={avatar.role} className="h-5 flex items-center">
            <div className="w-5 h-5 rounded-full overflow-hidden shrink-0">
              {avatar.imageUrl ? (
                <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover" />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-xs font-bold"
                  style={{ backgroundColor: avatar.primaryColor || '#475569', color: '#fff' }}
                >
                  {avatar.name.charAt(0)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 min-w-0 overflow-x-auto">
        <div className="flex flex-col gap-[3px]">
          {avatars.map(avatar => (
            <div key={avatar.role} className="flex gap-[3px]">
              {Array.from({ length: blocks }, (_, blockIndex) => {
                const count = grid.get(`${avatar.role}-${blockIndex}`) || 0;
                return (
                  <div
                    key={blockIndex}
                    className={`h-5 flex-1 min-w-[16px] rounded-sm transition-colors duration-500 ${getIntensity(count)}`}
                    title={`${avatar.name}: ${count} entries (${blockIndex * 5}-${(blockIndex + 1) * 5} min)`}
                  />
                );
              })}
            </div>
          ))}
        </div>
        {/* Time axis */}
        <div className="flex gap-[3px] mt-1">
          {Array.from({ length: blocks }, (_, i) => (
            <div key={i} className={`flex-1 min-w-[16px] text-center text-xs ${isDark ? 'text-white/20' : 'text-slate-300'}`}>
              {i * 5}m
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EngagementHeatmapWidget;
